import { degrees, PDFDocument, rgb, StandardFonts, type PDFFont, type PDFPage } from "pdf-lib";
import { ANNOTATION_COLOR_HEX, type AnnotationColor, type AnnotationInput } from "@klantpunt/shared";

/**
 * Markeringen (docs/DOSSIER.md): vormen staan genormaliseerd (0–1) op het blad zoals getoond, oorsprong
 * linksboven. Dezelfde meetkunde tekent ze op het scherm (SVG) en in de PDF bij downloaden.
 */

type Pt = [number, number];

/** Lijndikte en lettergrootte schalen mee met het blad: op A4 en A0 even goed leesbaar. */
export const strokeOf = (w: number, h: number) => Math.max(w, h) * 0.0025;
export const fontOf = (w: number, h: number) => Math.max(w, h) * 0.014;

/** Revisiewolk: boogjes rond een rechthoek (in getoonde punten), als lijn van punten. */
export function cloudPoints(x: number, y: number, w: number, h: number, page: { w: number; h: number }): Pt[] {
  const r = Math.max(4, Math.min(Math.max(page.w, page.h) * 0.008, Math.min(w, h) / 2));
  const corners: Pt[] = [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
  const normals: Pt[] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];
  const out: Pt[] = [];
  for (let e = 0; e < 4; e++) {
    const [ax, ay] = corners[e]!;
    const [bx, by] = corners[(e + 1) % 4]!;
    const [nx, ny] = normals[e]!;
    const len = Math.hypot(bx - ax, by - ay);
    const k = Math.max(1, Math.round(len / (2 * r)));
    const ux = (bx - ax) / len;
    const uy = (by - ay) / len;
    const rr = len / k / 2;
    for (let i = 0; i < k; i++) {
      const cx = ax + ux * rr * (2 * i + 1);
      const cy = ay + uy * rr * (2 * i + 1);
      for (let s = 0; s <= 8; s++) {
        if (s === 0 && out.length) continue;
        const t = (Math.PI * s) / 8;
        out.push([
          cx - rr * Math.cos(t) * ux + rr * Math.sin(t) * nx,
          cy - rr * Math.cos(t) * uy + rr * Math.sin(t) * ny,
        ]);
      }
    }
  }
  return out;
}

/** Tekst op het blad: regels, breedte (geschat zonder lettertype) en hoogte. */
export function textBox(text: string, fs: number, widthOf?: (line: string) => number) {
  const lines = text.split(/\r?\n/).slice(0, 12);
  const pad = fs * 0.4;
  const w = Math.max(...lines.map((l) => (widthOf ? widthOf(l) : l.length * fs * 0.56))) + 2 * pad;
  const h = lines.length * fs * 1.25 + 2 * pad;
  return { lines, pad, w, h };
}

export const svgPath = (pts: Pt[]) =>
  pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");

const hexToRgb = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
};

/** Helvetica kent alleen Westerse tekens; de rest wordt een vraagteken in plaats van een fout. */
const safe = (s: string) => s.replace(/[^\x20-\x7E\xA0-\xFF€–—‘’“”•…\n]/g, "?");

/**
 * Van getoonde punten (na draaiing van het blad) naar PDF-coördinaten. Zelfde afspraak als pdf.js
 * (viewport met rotatie 0/90/180/270 over de crop box).
 */
function pageMapper(page: PDFPage) {
  const box = page.getCropBox();
  const rot = (((page.getRotation().angle % 360) + 360) % 360) as 0 | 90 | 180 | 270;
  const W = box.width;
  const H = box.height;
  const shown = rot === 90 || rot === 270 ? { w: H, h: W } : { w: W, h: H };
  const map = ([dx, dy]: Pt): Pt => {
    switch (rot) {
      case 90:
        return [box.x + dy, box.y + dx];
      case 180:
        return [box.x + W - dx, box.y + dy];
      case 270:
        return [box.x + W - dy, box.y + H - dx];
      default:
        return [box.x + dx, box.y + H - dy];
    }
  };
  return { shown, map, rot };
}

function drawPolyline(page: PDFPage, pts: Pt[], map: (p: Pt) => Pt, color: string, thickness: number) {
  const c = hexToRgb(color);
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = map(pts[i - 1]!);
    const [bx, by] = map(pts[i]!);
    page.drawLine({ start: { x: ax, y: ay }, end: { x: bx, y: by }, thickness, color: c, lineCap: 1 });
  }
}

function drawAnnotation(page: PDFPage, a: AnnotationInput, font: PDFFont) {
  const { shown, map, rot } = pageMapper(page);
  const { w, h } = shown;
  const hex = ANNOTATION_COLOR_HEX[a.color as AnnotationColor];
  const sw = strokeOf(w, h);
  const s = a.shape;
  if (s.kind === "pen") {
    drawPolyline(
      page,
      s.points.map(([x, y]) => [x * w, y * h]),
      map,
      hex,
      sw,
    );
  } else if (s.kind === "line") {
    const pts: Pt[] = s.points.map(([x, y]) => [x * w, y * h]);
    if (s.closed && pts.length > 2) {
      // Vlak licht ingekleurd; pad in PDF-punten met y omgekeerd (drawSvgPath rekent y naar beneden).
      const path = pts
        .map(map)
        .map(([x, y], i) => `${i ? "L" : "M"}${x} ${-y}`)
        .join(" ");
      page.drawSvgPath(`${path} Z`, { x: 0, y: 0, color: hexToRgb(hex), opacity: 0.15 });
      pts.push(pts[0]!);
    }
    drawPolyline(page, pts, map, hex, sw);
  } else if (s.kind === "cloud") {
    drawPolyline(page, cloudPoints(s.x * w, s.y * h, s.w * w, s.h * h, { w, h }), map, hex, sw);
  } else {
    const fs = fontOf(w, h);
    const text = safe(s.text);
    const box = textBox(text, fs, (l) => font.widthOfTextAtSize(l, fs));
    const tx = s.x * w;
    const ty = s.y * h;
    const [bx, by] = map([tx, ty + box.h]);
    page.drawRectangle({
      x: bx,
      y: by,
      width: box.w,
      height: box.h,
      rotate: degrees(rot),
      color: rgb(1, 1, 1),
      opacity: 0.9,
      borderColor: hexToRgb(hex),
      borderWidth: sw * 0.6,
    });
    box.lines.forEach((line, i) => {
      const [lx, ly] = map([tx + box.pad, ty + box.pad + (i + 1) * fs * 1.25 - fs * 0.3]);
      page.drawText(line, { x: lx, y: ly, size: fs, font, color: hexToRgb(hex), rotate: degrees(rot) });
    });
  }
}

/** PDF met de markeringen erin getekend, plus een stempel dat dit nog geen nieuwe revisie is. */
export async function pdfWithMarkup(bytes: ArrayBuffer, items: AnnotationInput[], stamp: string): Promise<Blob> {
  const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const pages = pdf.getPages();
  const marked = new Set<number>();
  for (const a of items) {
    const page = pages[a.page - 1];
    if (!page) continue;
    drawAnnotation(page, a, font);
    marked.add(a.page);
  }
  for (const n of marked) {
    const page = pages[n - 1]!;
    const { shown, map, rot } = pageMapper(page);
    const fs = fontOf(shown.w, shown.h) * 0.8;
    const [x, y] = map([shown.w * 0.015, shown.h * 0.015 + fs]);
    page.drawText(safe(stamp), {
      x,
      y,
      size: fs,
      font,
      color: hexToRgb(ANNOTATION_COLOR_HEX.rood),
      rotate: degrees(rot),
    });
  }
  return new Blob([(await pdf.save()) as Uint8Array<ArrayBuffer>], { type: "application/pdf" });
}
